/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { PersonEntry, PersonPaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class PeopleApi extends BaseApi {
    createPerson(personBodyCreate, opts) {
        throwIfNotDefined(personBodyCreate, 'personBodyCreate');
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/people',
            queryParams,
            bodyParam: personBodyCreate,
            returnType: PersonEntry
        });
    }
    deleteAvatarImage(personId) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        return this.delete({
            path: '/people/{personId}/avatar',
            pathParams
        });
    }
    getAvatarImage(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment,
            placeholder: opts === null || opts === void 0 ? void 0 : opts.placeholder
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince
        };
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/people/{personId}/avatar',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    getPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}',
            pathParams,
            queryParams,
            returnType: PersonEntry
        });
    }
    listPeople(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people',
            queryParams,
            returnType: PersonPaging
        });
    }
    requestPasswordReset(personId, clientBody) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(clientBody, 'clientBody');
        const pathParams = {
            personId
        };
        return this.post({
            path: '/people/{personId}/request-password-reset',
            pathParams,
            bodyParam: clientBody
        });
    }
    resetPassword(personId, passwordResetBody) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(passwordResetBody, 'passwordResetBody');
        const pathParams = {
            personId
        };
        return this.post({
            path: '/people/{personId}/reset-password',
            pathParams,
            bodyParam: passwordResetBody
        });
    }
    updateAvatarImage(personId, contentBodyUpdate) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(contentBodyUpdate, 'contentBodyUpdate');
        const pathParams = {
            personId
        };
        const contentTypes = ['application/octet-stream'];
        return this.put({
            path: '/people/{personId}/avatar',
            pathParams,
            bodyParam: contentBodyUpdate,
            contentTypes
        });
    }
    updatePerson(personId, personBodyUpdate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(personBodyUpdate, 'personBodyUpdate');
        const pathParams = {
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/people/{personId}',
            pathParams,
            queryParams,
            bodyParam: personBodyUpdate,
            returnType: PersonEntry
        });
    }
}
